import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewRef } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { CartActions, StateWithMultiCart } from '@spartacus/cart/base/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { CheckoutDeliveryModesFacade } from '@spartacus/checkout/base/root';
import { AuthService, CmsService, UserIdService } from '@spartacus/core';
import { Observable, Subscription, combineLatest, iif, of } from 'rxjs';
import { debounceTime, retryWhen, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { CartExtend } from 'src/app/model/cart.model';
import { B2bunitService } from 'src/app/service/b2bunit/b2bunit.service';
import { BaseSiteConfigService, CHANGE_DELIVERY_ADDRESS } from 'src/app/service/base-site/base-site-config.service';
import { CartExtendService } from 'src/app/service/cart/cart-extend.service';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { MyAccountPermissionService } from 'src/app/service/myaccount/my-account-permission.service';
import {
  EDIT_DEFAULT_DELIVERY_ADDRESS,
  MyaccountFeaturesConfigService,
} from 'src/app/service/myaccount/myaccount-features-config.service';
import { MyaccountPersonalInfoService } from 'src/app/service/myaccount/myaccount-personal-info.service';
import { SHIP_TO_ADDRESS, SnahCountryService } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';

@Component({
  selector: 'app-checkout-shipping-address',
  templateUrl: './checkout-shipping-address.component.html',
  styleUrls: ['./checkout-shipping-address.component.scss'],
  standalone: false,
})
export class CheckoutShippingAddressComponent implements OnInit, OnDestroy {
  @ViewChild('orderType', { static: true }) orderType: ElementRef<HTMLElement>;
  private subscription = new Subscription();
  addressSelectionActive = false;
  defaultShippingAddress: any;
  allShippingAddresses: any;
  shippingAddresses: any;
  deliveryAddress: any;
  selectedDeliveryAddress: any;
  isButtonDisabled = true;
  isButtonLoading = false;
  currentAddress = this.fb.group({
    addressId: '',
  });
  userCurrent$: Observable<string> = this.userIdService.getUserId();
  activeCart$: Observable<string> = this.cartService.getActiveCartId();
  cart$: Observable<CartExtend>;
  placeOrderSubscription$: Subscription;
  hasChangedAddress$: Subscription;
  canContinue = false;
  matchedAddress: any;
  hasChangedAddress = false;
  selectedIdAddress: any;
  radioSelector: any;
  showActiveSnahCountry$: Observable<boolean>;
  isActiveCountrySnah: boolean;

  activeCountry$: Observable<string> = this.countryService.getActive();

  showSNAHShipToAddress$: Observable<boolean> = this.snahCountryService.getSnahFeatures(SHIP_TO_ADDRESS);
  shipToAddresses$ = this.myAccountPermissionService.getShipToAddress();
  unitAddresses$ = this.b2bUnitService.getB2BUnitAddresses();
  currentAddressSub$: Subscription;

  isChangeDeliveryAddressEnabled$: Observable<boolean> = this.baseSiteConfigService
    .getBaseSiteConfigValues$([CHANGE_DELIVERY_ADDRESS])
    .pipe(
      shareReplay(),
      switchMap((configs) =>
        combineLatest([
          of(
            configs &&
              configs
                .filter((config: any) => config.key === CHANGE_DELIVERY_ADDRESS)
                .map((res: any) => res.value === 'true')[0]
          ),
          this.activeCountry$,
        ])
      ),
      switchMap(([isChangeEnabled, activeCountry]) =>
        iif(
          () => isChangeEnabled,
          this.myaccountFeaturesConfigSerivce.isMyaccountFeatureEnabled$(activeCountry, EDIT_DEFAULT_DELIVERY_ADDRESS),
          of(false)
        )
      )
    );

  constructor(
    protected b2bUnitService: B2bunitService,
    protected cartExtendService: CartExtendService,
    protected checkoutDeliveryService: CheckoutDeliveryModesFacade,
    protected checkoutServiceExtends: CheckoutServiceExtends,
    public fb: FormBuilder,
    public cartService: ActiveCartFacade,
    private cdr: ChangeDetectorRef,
    private store: Store<StateWithMultiCart>,
    private authService: AuthService,
    private userIdService: UserIdService,
    private cmsService: CmsService,
    private componentService: ComponentService,
    private baseSiteConfigService: BaseSiteConfigService,
    private countryService: ShimanoCountryService,
    private myaccountFeaturesConfigSerivce: MyaccountFeaturesConfigService,
    private snahCountryService: SnahCountryService,
    private myAccountPermissionService: MyAccountPermissionService,
    private myaccountPersonalInfoService: MyaccountPersonalInfoService,
    private storageService: StorageService
  ) {
    this.currentAddressSub$ = this.showSNAHShipToAddress$.subscribe((isActiveCountrySnah) => {
      if (isActiveCountrySnah) {
        this.shipToAddresses$.pipe(take(1)).subscribe((res) => this.setDeliveryAddress(res));
      } else {
        this.unitAddresses$.pipe(take(1)).subscribe((res) => this.setDeliveryAddress(res));
      }
    });
  }

  get userCurrent(): string {
    let userCurrent: string;
    this.userCurrent$.pipe(take(1)).subscribe((userId) => (userCurrent = userId));
    return userCurrent;
  }
  get activeCartId() {
    let activeCartId: string;
    this.activeCart$.pipe(take(1)).subscribe((activeCart) => (activeCartId = activeCart));
    return activeCartId;
  }

  setDeliveryAddress(address: any) {
    // set shippingAddresses
    this.shippingAddresses = address.addresses.filter(
      (address: any) =>
        address.shippingAddress &&
        address.visibleInAddressBook &&
        !address.defaultAddress &&
        address.approvalStatus === 'APPROVED'
    );

    // set defaultShippingAddress
    this.defaultShippingAddress = address.addresses.find(
      (address: any) => address.defaultAddress && address.shippingAddress
    );

    // set allShippingAddresses
    this.allShippingAddresses = [this.defaultShippingAddress, ...this.shippingAddresses];
    if (typeof this.defaultShippingAddress !== 'undefined') {
      this.currentAddress.get('addressId').patchValue(this.defaultShippingAddress.id);
    }
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.placeOrderSubscription$ = this.checkoutServiceExtends.placeOrder$.subscribe((status) => {
      if (status) {
        this.canContinue = true;
        this.addressSelectionActive = false;
      }
    });

    this.hasChangedAddress$ = this.componentService.holdDeliveryAddress$.subscribe((status) => {
      this.hasChangedAddress = status;
    });

    setTimeout(() => {
      if (!this.hasChangedAddress) {
        this.cartExtendService
          .getCurrentCart()
          .pipe(
            take(1),
            retryWhen((err) => err.pipe(tap(), debounceTime(1000)))
          )
          .subscribe((res) => {
            if (res && res.deliveryAddress) {
              // tslint:disable-next-line: no-string-literal
              if (typeof res['deliveryAddress'] !== 'undefined') {
                // tslint:disable-next-line: no-string-literal
                this.deliveryAddress = res['deliveryAddress'];
              } else {
                this.getDeliveryAddress();
              }
            } else {
              this.getDeliveryAddress();
            }
            if (typeof this.allShippingAddresses !== 'undefined') {
              for (const addresses of this.allShippingAddresses) {
                if (typeof addresses !== 'undefined') {
                  if (addresses.addressId === this.deliveryAddress.addressId && !this.hasChangedAddress) {
                    this.matchedAddress = this.deliveryAddress.formattedAddress;
                    break;
                  } else {
                    if (!this.hasChangedAddress) {
                      this.matchedAddress = this.deliveryAddress.formattedAddress;
                    }
                  }
                }
              }
            }
            this.cmsService.getCurrentPage().subscribe((currentPage) => {
              if (currentPage && currentPage.template.includes('ShimanoCheckoutSummaryPageTemplate')) {
                if (!(this.cdr as ViewRef).destroyed) {
                  const el: HTMLElement = this.orderType.nativeElement;
                  el.click();
                  this.cdr.detectChanges();
                }
              }
            });
          });
      }
    }, 1000);
  }

  getDeliveryAddress() {
    if (this.storageService.getSessionStorageItem('saddID') !== null) {
      this.deliveryAddress.formattedAddress = this.storageService.getSessionStorageItem('saddFormatted');
      this.deliveryAddress.addressId = this.storageService.getSessionStorageItem('saddID');
    } else {
      this.unitAddresses$.pipe(take(1)).subscribe((resdefault) => {
        if (resdefault) {
          this.subscription.add(
            this.cartExtendService
              .saveAddressToCart(this.defaultShippingAddress.addressId, this.activeCartId)
              .subscribe()
          );
          this.deliveryAddress = this.defaultShippingAddress;
        }
      });
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.placeOrderSubscription$) {
      this.placeOrderSubscription$.unsubscribe();
    }
    if (this.hasChangedAddress$) {
      this.hasChangedAddress$.unsubscribe();
    }
    this.currentAddressSub$?.unsubscribe();
  }

  changeAddressSection() {
    this.addressSelectionActive = true;
    this.isButtonDisabled = true;
  }

  onSubmit() {
    this.isButtonDisabled = true;
    this.isButtonLoading = true;
    this.subscription.add(
      this.cartExtendService
        .saveAddressToCart(this.currentAddress.get('addressId').value, this.activeCartId)
        .subscribe((res: any) => {
          if (res) {
            this.isButtonDisabled = false;
            this.isButtonLoading = false;
            this.deliveryAddress = res.deliveryAddress;
            this.matchedAddress = this.deliveryAddress.formattedAddress;
            this.storageService.setSessionStorageItem('saddID', this.deliveryAddress.addressId);
            this.storageService.setSessionStorageItem('saddFormatted', this.deliveryAddress.formattedAddress);
            this.addressSelectionActive = false;
            this.store.dispatch(
              new CartActions.LoadCart({
                userId: this.userCurrent,
                cartId: res.code,
              })
            );
            this.cart$ = this.cartService.getActive();
            if (!(this.cdr as ViewRef).destroyed) {
              this.cdr.detectChanges();
            }
          }
        })
    );
  }

  onCancel() {
    this.addressSelectionActive = false;
    this.radioSelector = this.selectedIdAddress;
  }

  idSelector(addressId: any, selectedIdAddress: any) {
    if (addressId !== undefined) {
      return addressId;
    } else {
      return selectedIdAddress;
    }
  }

  checkValue(idValue: any) {
    if (idValue !== undefined) {
      this.radioSelector = idValue;
      this.isButtonDisabled = false;
    }
  }

  showAddress(addresses: any): Observable<string> {
    return this.myaccountPersonalInfoService.formatAddress(addresses);
  }
}
